import { HugeiconsIcon } from '@hugeicons/react';
import {
  UserMultipleIcon,
  Delete02Icon,
  Add01Icon,
  Settings02Icon,
  CheckmarkCircle02Icon,
} from '@hugeicons/core-free-icons';
import { useEffect, useState, useCallback } from 'react';
import { api, apiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { PageHeader, EmptyState } from '@/components/common/Bits';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { MotionSection } from '@/components/shared/MotionSection';

const ROLE_LABELS = {
  miembro: 'Miembro',
};

export default function CommitteeConfig() {
  const { can } = useAuth();
  const [members, setMembers] = useState(null);
  const [config, setConfig] = useState(null);

  const load = useCallback(async () => {
    try {
      const [membersRes, configRes] = await Promise.all([api.get('/committee/members'), api.get('/committee/config')]);
      setMembers(membersRes.data);
      setConfig(configRes.data);
    } catch {
      toast.error('Error al cargar configuración del comité');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!members || !config) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-10 space-y-4">
        <Skeleton className="h-20 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-10 space-y-8">
      <PageHeader
        title="Configuración del Comité"
        subtitle="Define quiénes son los miembros del comité de IA y los parámetros de votación."
        testId="committee-config-header"
      />

      <MotionSection>
        <div className="grid gap-6 grid-cols-1 lg:grid-cols-3">
          {/* Config card */}
          <Card className="shadow-card border-border lg:col-span-1 overflow-hidden">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <HugeiconsIcon icon={Settings02Icon} className="h-4 w-4 text-primary" aria-hidden="true" />
                Parámetros
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ConfigForm config={config} onSaved={load} canEdit={can.admin} />
            </CardContent>
          </Card>

          {/* Members card */}
          <Card className="shadow-card border-border lg:col-span-2 overflow-hidden">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <HugeiconsIcon icon={UserMultipleIcon} className="h-4 w-4 text-primary" aria-hidden="true" />
                  Miembros ({members.length})
                </CardTitle>
                {can.admin && <AddMemberDialog members={members} onAdded={load} />}
              </div>
            </CardHeader>
            <CardContent>
              {members.length === 0 ? (
                <EmptyState
                  icon={UserMultipleIcon}
                  title="Sin miembros"
                  subtitle="Agregue al menos un miembro para poder enviar iniciativas al comité."
                />
              ) : (
                <div className="space-y-2">
                  {members.map((m) => (
                    <MemberRow key={m.id} member={m} onRemoved={load} onRoleChanged={load} canEdit={can.admin} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </MotionSection>
    </div>
  );
}

function ConfigForm({ config, onSaved, canEdit }) {
  const [name, setName] = useState(config.name);
  const [description, setDescription] = useState(config.description || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/committee/config', {
        quorum_required: config.quorum_required,
        name,
        description,
      });
      toast.success('Configuración actualizada');
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="committee-name" className="text-xs">
          Nombre
        </Label>
        <Input
          id="committee-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={!canEdit}
          className="h-9 text-sm"
          aria-label="Nombre del comité"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="committee-desc" className="text-xs">
          Descripción
        </Label>
        <Textarea
          id="committee-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={!canEdit}
          className="text-sm min-h-[60px]"
          aria-label="Descripción del comité"
        />
      </div>
      <div className="rounded-lg bg-muted/50 p-3">
        <p className="text-[11px] text-muted-foreground">
          <span className="font-medium">Quórum:</span> Se calcula automáticamente como mayoría simple de los miembros
          registrados.
        </p>
      </div>
      {canEdit && (
        <Button onClick={save} disabled={saving} size="sm" className="w-full" aria-label="Guardar configuración">
          <HugeiconsIcon icon={CheckmarkCircle02Icon} className="h-3.5 w-3.5 mr-1.5" />
          Guardar
        </Button>
      )}
    </div>
  );
}

function MemberRow({ member, onRemoved, onRoleChanged, canEdit }) {
  const remove = async () => {
    try {
      await api.delete(`/committee/members/${member.user_id}`);
      toast.success('Miembro eliminado del comité');
      onRemoved();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary/20 transition-colors">
      <div className="size-9 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
        <span className="text-xs font-semibold">
          {member.user_name
            ?.split(' ')
            .map((n) => n[0])
            .join('')
            .slice(0, 2)
            .toUpperCase()}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{member.user_name}</p>
        <p className="text-[11px] text-muted-foreground truncate">{member.user_email}</p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Badge variant="outline" className="text-[10px] hidden sm:inline-flex">
          Miembro
        </Badge>
        {canEdit && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                aria-label={`Eliminar a ${member.user_name} del comité`}>
                <HugeiconsIcon icon={Delete02Icon} className="h-4 w-4" aria-hidden="true" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Eliminar miembro</AlertDialogTitle>
                <AlertDialogDescription>
                  ¿Eliminar a {member.user_name} del comité? Ya no podrá votar en nuevas decisiones.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground">
                  Eliminar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </div>
  );
}

function AddMemberDialog({ members, onAdded }) {
  const [open, setOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [role, setRole] = useState('miembro');
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const memberIds = new Set(members.map((m) => m.user_id));

  useEffect(() => {
    if (search.length < 2) {
      setSearchResults([]);
      return;
    }
    const timeout = setTimeout(() => {
      api
        .get(`/users/search?q=${encodeURIComponent(search)}`)
        .then(({ data }) => setSearchResults(data || []))
        .catch(() => setSearchResults([]));
    }, 300);
    return () => clearTimeout(timeout);
  }, [search]);

  const availableUsers = searchResults.filter((u) => !memberIds.has(u.id));

  const add = async () => {
    if (!selectedUser) return;
    try {
      await api.post('/committee/members', { user_id: selectedUser, role_in_committee: 'miembro' });
      toast.success('Miembro agregado al comité');
      setOpen(false);
      setSelectedUser('');
      setSearch('');
      setSearchResults([]);
      onAdded();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="gap-1.5" aria-label="Agregar miembro al comité">
          <HugeiconsIcon icon={Add01Icon} className="h-3.5 w-3.5" /> Agregar
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Agregar miembro al comité</DialogTitle>
          <DialogDescription>
            Seleccione un usuario activo para agregarlo como miembro del comité de IA.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Buscar usuario</Label>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Escriba al menos 2 caracteres..."
              className="h-9 text-sm"
              aria-label="Buscar usuario por nombre o email"
            />
          </div>
          <div
            className="max-h-48 overflow-y-auto border border-border rounded-lg"
            role="listbox"
            aria-label="Usuarios disponibles">
            {availableUsers.length === 0 ? (
              <p className="text-xs text-muted-foreground p-3 text-center">
                {search ? 'Sin resultados' : 'No hay usuarios disponibles'}
              </p>
            ) : (
              availableUsers.slice(0, 20).map((u) => (
                <button
                  key={u.id}
                  type="button"
                  role="option"
                  aria-selected={selectedUser === u.id}
                  onClick={() => setSelectedUser(u.id)}
                  className={`w-full text-left px-3 py-2.5 text-sm hover:bg-muted/50 transition-colors border-b border-border/50 last:border-0 ${
                    selectedUser === u.id ? 'bg-muted/60 border-l-2 border-l-primary' : ''
                  }`}>
                  <p className="font-medium text-xs text-foreground">{u.name}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {u.email} · {u.department || 'Sin área'}
                  </p>
                </button>
              ))
            )}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={add} disabled={!selectedUser} aria-label="Confirmar agregar miembro">
            Agregar miembro
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
